import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { API_BASE_URL } from "../config";
import { toast } from "react-toastify";
import { FiCheckSquare, FiSquare, FiSend, FiRefreshCw, FiSearch, FiUploadCloud, FiX } from "react-icons/fi";
import {
    Button,
    Card,
    Badge,
    PageHeader,
    EmptyState,
    Tooltip,
    useConfirm,
    TableSkeleton,
} from "./ui";

const jenisTone = (jenis) => (jenis === "new" ? "success" : jenis === "old" ? "warning" : "slate");

export default function UpdatePanel() {
    const confirm = useConfirm();

    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStores, setSelectedStores] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState(null);

    const fetchStores = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('toko')
                .select('kode_toko, email, jenis')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setStores(data || []);
        } catch (error) {
            toast.error("Error fetching stores: " + error.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchStores();
    }, []);

    const filteredStores = stores.filter(store =>
        store.kode_toko.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (store.email && store.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const toggleSelect = (code) => {
        if (selectedStores.includes(code)) {
            setSelectedStores(selectedStores.filter(c => c !== code));
        } else {
            setSelectedStores([...selectedStores, code]);
        }
    };

    const toggleSelectAll = () => {
        if (selectedStores.length === filteredStores.length) {
            setSelectedStores([]);
        } else {
            setSelectedStores(filteredStores.map(s => s.kode_toko));
        }
    };

    const handleSendUpdate = async () => {
        if (selectedStores.length === 0) {
            toast.warn("Pilih minimal satu toko");
            return;
        }

        const ok = await confirm({
            title: "Kirim Update",
            message: `Kirim update ke ${selectedStores.length} toko terpilih?`,
            confirmLabel: "Kirim Update",
        });
        if (!ok) return;

        setIsSending(true);
        setResults(null);

        const toastId = toast.loading(`Mengirim update ke ${selectedStores.length} toko...`);

        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/broadcast-update`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ stores: selectedStores })
            });

            const data = await res.json();

            if (data.success) {
                toast.update(toastId, { render: "✅ Update berhasil dikirim!", type: "success", isLoading: false, autoClose: 3000 });
                setResults(data.results);
            } else {
                toast.update(toastId, { render: "❌ Gagal: " + data.error, type: "error", isLoading: false, autoClose: 5000 });
            }

        } catch (error) {
            toast.update(toastId, { render: "❌ Error: " + error.message, type: "error", isLoading: false, autoClose: 5000 });
        }

        setIsSending(false);
    };

    const allSelected = filteredStores.length > 0 && selectedStores.length === filteredStores.length;

    return (
        <div className="space-y-6 animate-fade-in pb-24">
            <PageHeader
                title="Update Program"
                subtitle="Kirim update program ke banyak toko sekaligus"
                icon={FiUploadCloud}
                actions={
                    <Tooltip label="Muat ulang">
                        <Button variant="secondary" size="icon" onClick={fetchStores} aria-label="Muat ulang">
                            <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        </Button>
                    </Tooltip>
                }
            />

            {/* Results */}
            {results && (
                <Card className="p-5">
                    <h3 className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Hasil Pengiriman</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="rounded-xl border border-success-100 bg-success-50 p-4 dark:border-success-500/20 dark:bg-success-500/10">
                            <p className="mb-2 text-sm font-medium text-success-700 dark:text-success-400">
                                Berhasil ({results.success.length})
                            </p>
                            <div className="max-h-32 overflow-y-auto font-mono text-xs text-slate-600 scrollbar-slim dark:text-slate-300">
                                {results.success.join(", ") || "-"}
                            </div>
                        </div>
                        <div className="rounded-xl border border-danger-100 bg-danger-50 p-4 dark:border-danger-500/20 dark:bg-danger-500/10">
                            <p className="mb-2 text-sm font-medium text-danger-700 dark:text-danger-400">
                                Gagal ({results.failed.length})
                            </p>
                            <div className="max-h-32 overflow-y-auto text-xs text-slate-600 scrollbar-slim dark:text-slate-300">
                                {results.failed.length === 0
                                    ? "-"
                                    : results.failed.map((f, i) => (
                                        <div key={i}>{f.code}: {f.error}</div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Recipients */}
            <Card>
                <div className="flex flex-col gap-3 border-b border-slate-100 p-4 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
                    <div className="relative w-full lg:max-w-sm">
                        <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari toko..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="field-base pl-10"
                        />
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                            Terpilih <span className="font-semibold text-slate-800 dark:text-slate-200">{selectedStores.length}</span> / {filteredStores.length}
                        </span>
                        <Button variant="ghost" size="sm" onClick={toggleSelectAll} disabled={filteredStores.length === 0}>
                            {allSelected ? "Batal Pilih Semua" : "Pilih Semua"}
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <TableSkeleton rows={8} columns={4} />
                ) : filteredStores.length === 0 ? (
                    <EmptyState title="Tidak ada toko ditemukan" description="Coba kata kunci lain." />
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {filteredStores.map((store) => {
                            const isSelected = selectedStores.includes(store.kode_toko);
                            return (
                                <button
                                    key={store.kode_toko}
                                    onClick={() => toggleSelect(store.kode_toko)}
                                    className={`flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors ${
                                        isSelected ? "bg-primary-50/60 dark:bg-primary-500/10" : "hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                                    }`}
                                >
                                    {isSelected ? (
                                        <FiCheckSquare className="h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                                    ) : (
                                        <FiSquare className="h-5 w-5 flex-shrink-0 text-slate-300 dark:text-slate-600" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{store.kode_toko}</p>
                                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{store.email}</p>
                                    </div>
                                    <Badge tone={jenisTone(store.jenis)}>{store.jenis || "N/A"}</Badge>
                                </button>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Sticky action bar */}
            {selectedStores.length > 0 && (
                <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200/80 bg-white/90 px-4 py-3 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/90 lg:pl-[var(--sidebar-w)]">
                    <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-600 text-sm font-bold text-white">
                                {selectedStores.length}
                            </span>
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                toko dipilih
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" icon={FiX} onClick={() => setSelectedStores([])}>
                                Batal
                            </Button>
                            <Button icon={FiSend} loading={isSending} onClick={handleSendUpdate}>
                                {isSending ? "Mengirim..." : "Kirim Update"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
